import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Field, Select, Spinner, type Tone } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useStoreChoice } from '../lib/store-choice';
import { useMe, usePermissions } from '../lib/session';
import type { CostCoverage, FinanceReport } from '../lib/types-faz8';
import { downloadReport } from '../components/reports/download';
import { Figure, Section, Table, td, tdR, th, thR, usePct } from '../components/reports/blocks';
import { PeriodControls, usePeriod } from '../components/reports/period';
import { PrintButton, PrintHeader } from '../components/reports/print';

/**
 * Finans raporu (F8-05, ADR-805): kârlılık (sipariş esası, KDV hariç), nakit akışı (olay tarihi), KDV özeti, alacaklar,
 * komisyon, kanal ve ürün kârlılığı, ödeme mutabakatı, maliyeti girilmemiş ürünler. Eksik girdi uydurulmaz; her kalemin
 * veri durumu yazılır. Tanımlar: docs/faz-8/REPORTS_FINANCE_TR.md#finans-raporu.
 */

const CHANNELS = ['web', 'b2b', 'manual', 'api'] as const;
const SEGMENTS = ['retail', 'dealer'] as const;
type Status = 'complete' | 'partial' | 'missing';
const STATUS_TONE: Record<Status, Tone> = { complete: 'moss', partial: 'amber', missing: 'slate' };

/** Gelire oranı (baz puan); gelir sıfırsa yok. Yalnızca gösterim oranıdır, para hesabı değildir. */
const shareBps = (amount: number, revenue: number): number | null =>
  revenue > 0 ? Math.round((amount * 10_000) / revenue) : null;

export function FinanceReportPage() {
  const t = useT();
  const money = useMoney();
  const period = usePeriod();
  const { stores } = useStoreChoice();
  const [storeId, setStoreId] = useState('');
  const [channel, setChannel] = useState('');
  const [segment, setSegment] = useState('');
  const params = new URLSearchParams({ from: period.from, to: period.to });
  if (storeId) params.set('storeId', storeId);
  if (channel) params.set('channel', channel);
  if (segment) params.set('segment', segment);
  const q = useQuery({
    queryKey: ['finance-report', params.toString()],
    queryFn: () => api.get<FinanceReport>(`/v1/admin/reports/finance?${params}`),
    placeholderData: keepPreviousData,
  });
  const cp = new URLSearchParams({ from: period.from, to: period.to });
  if (storeId) cp.set('storeId', storeId);
  const coverage = useQuery({
    queryKey: ['cost-coverage', cp.toString()],
    queryFn: () => api.get<CostCoverage>(`/v1/admin/reports/costs/coverage?${cp}`),
    placeholderData: keepPreviousData,
  });
  const tl = (n: number | null) => money(n, 'TRY');
  const pct = usePct();
  const margin = (bps: number | null) => t('admin.financeReport.marginOf', { pct: pct(bps) });
  const d = q.data;
  return (
    <PageFrame
      helpKey="page.finance_report"
      printable
      actions={
        <span className="inline-flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() =>
              void downloadReport(`/v1/admin/reports/finance?${params}&format=csv`, 'finans.csv')
            }
          >
            {t('admin.reports.csv')}
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              void downloadReport(`/v1/admin/reports/finance?${params}&format=xlsx`, 'finans.xlsx')
            }
          >
            {t('admin.reports.xlsx')}
          </Button>
          <HelpTip k="reports.download" />
          <PrintButton />
        </span>
      }
    >
      <PrintHeader title={t('admin.financeReport.navLabel')} period={period} />
      <div className="flex flex-wrap items-end gap-4 print:hidden">
        <PeriodControls period={period} showInterval={false} />
        {stores.length > 1 && (
          <Field label={t('admin.salesReport.store')} help={<HelpTip k="sales_report.store" />}>
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                <option value="">{t('admin.reports.all')}</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.salesReport.channel')} help={<HelpTip k="sales_report.channel" />}>
          {(p) => (
            <Select {...p} value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">{t('admin.reports.all')}</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`admin.salesReport.channels.${c}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.salesReport.segment')} help={<HelpTip k="sales_report.segment" />}>
          {(p) => (
            <Select {...p} value={segment} onChange={(e) => setSegment(e.target.value)}>
              <option value="">{t('admin.reports.all')}</option>
              {SEGMENTS.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.salesReport.segments.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className={q.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <div className="space-y-1 text-sm text-ink-3">
            <p>{t('admin.reports.currencyNote')}</p>
            {d.period.partial && <p>{t('admin.reports.partial')}</p>}
            {d.fxMissing > 0 && (
              <p role="alert" className="text-amber">
                {t('admin.salesReport.fxMissing', { count: d.fxMissing })}
              </p>
            )}
          </div>

          <section
            aria-label={t('admin.salesReport.tilesTitle')}
            className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            <Figure label={t('admin.financeReport.tiles.revenue')} value={tl(d.profit.revenue)} />
            <Figure
              label={t('admin.financeReport.tiles.grossProfit')}
              value={tl(d.profit.grossProfit)}
              sub={
                d.profit.cogs.status === 'complete'
                  ? margin(shareBps(d.profit.grossProfit, d.profit.revenue))
                  : t('admin.financeReport.grossUnknown')
              }
            />
            <Figure
              label={t('admin.financeReport.tiles.contribution')}
              value={tl(d.profit.contribution)}
              sub={margin(d.profit.contributionBps)}
            />
            <Figure
              label={t('admin.financeReport.tiles.operatingProfit')}
              value={tl(d.profit.operatingProfit)}
              sub={margin(d.profit.operatingBps)}
            />
            <Figure
              label={t('admin.financeReport.tiles.netCash')}
              value={tl(d.cash.net)}
              help={<HelpTip k="finance_report.cash" />}
            />
          </section>

          <ProfitTable d={d} />

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <CashTable d={d} filtered={!!(storeId || channel || segment === 'retail')} />
            <div className="space-y-6">
              <VatTable d={d} />
              <Balances d={d} />
            </div>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <FeesTable d={d} />
            <ChannelsTable d={d} />
          </div>

          <ProductsTable d={d} />

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <MissingCosts coverage={coverage.data} error={coverage.error} />
            <Reconciliation d={d} />
          </div>
        </div>
      )}
    </PageFrame>
  );
}

// ------------------------------------------------------------------------------------------ Parçalar

function StatusBadge({ status }: { status: Status }) {
  const t = useT();
  return <Badge tone={STATUS_TONE[status]}>{t(`admin.financeReport.statuses.${status}`)}</Badge>;
}

/** İki sütunlu tutar tablosu satırı (kalem · tutar); `sub` girintili alt kalem, `strong` ara toplam. */
function AmountRow({
  label,
  amount,
  sub,
  strong,
  extra,
}: {
  label: string;
  amount: string;
  sub?: boolean;
  strong?: boolean;
  extra?: ReactNode;
}) {
  return (
    <tr className={strong ? 'border-t-2 border-line font-medium' : 'border-t border-line'}>
      <th
        scope="row"
        className={`${td} text-start ${strong ? 'font-medium' : 'font-normal'} ${sub ? 'ps-8 text-ink-2' : ''}`}
      >
        {label}
      </th>
      <td className={`${tdR} ${sub ? 'text-ink-2' : ''}`}>{amount}</td>
      {extra}
    </tr>
  );
}

function ProfitTable({ d }: { d: FinanceReport }) {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const pct = usePct();
  const p = d.profit;
  const tl = (n: number) => money(n, 'TRY');
  const cost = (n: number) => money(n === 0 ? 0 : -n, 'TRY');
  const share = (n: number) => pct(shareBps(n, p.revenue));
  type Row = { k: string; amount: string; value: number; status?: Status; strong?: boolean; sub?: boolean };
  const rows: Row[] = [
    { k: 'revenue', amount: tl(p.revenue), value: p.revenue, strong: true },
    // Hiç maliyet verisi yoksa tutar bilinmiyor ("—"); sıfır yazılmaz.
    {
      k: 'cogs',
      amount: p.cogs.status === 'missing' ? '—' : cost(p.cogs.amount),
      value: p.cogs.amount,
      status: p.cogs.status,
    },
    { k: 'grossProfit', amount: tl(p.grossProfit), value: p.grossProfit, strong: true },
    { k: 'shipping', amount: cost(p.shipping.amount), value: p.shipping.amount, status: p.shipping.status },
    { k: 'fees', amount: cost(p.fees.amount), value: p.fees.amount, status: p.fees.status },
    { k: 'ads', amount: cost(p.ads.amount), value: p.ads.amount, status: p.ads.status },
    // Ödeme itirazları (F8-13): yalnızca kaydı olan dönemde gösterilir.
    ...(p.disputes.amount > 0
      ? [{ k: 'disputes', amount: cost(p.disputes.amount), value: p.disputes.amount }]
      : []),
    { k: 'contribution', amount: tl(p.contribution), value: p.contribution, strong: true },
    { k: 'expenses', amount: cost(p.expenses.amount), value: p.expenses.amount },
  ];
  const categories = Object.entries(p.expenses.byCategory).sort((a, b) => b[1] - a[1]);
  const notes: string[] = [];
  if (p.cogs.unknownRevenue > 0)
    notes.push(
      p.grossMarginKnownBps === null
        ? t('admin.financeReport.notes.cogsUnknownAll', { amount: tl(p.cogs.unknownRevenue) })
        : t('admin.financeReport.notes.cogsUnknown', {
            amount: tl(p.cogs.unknownRevenue),
            pct: pct(p.grossMarginKnownBps),
          }),
    );
  if (p.cogs.lateAmount > 0)
    notes.push(t('admin.financeReport.notes.cogsLate', { amount: tl(p.cogs.lateAmount) }));
  if (p.shipping.estimated > 0)
    notes.push(t('admin.financeReport.notes.shippingEstimated', { amount: tl(p.shipping.estimated) }));
  if (p.shipping.unknownShipments > 0)
    notes.push(
      t('admin.financeReport.notes.shippingUnknown', { count: formatNumber(p.shipping.unknownShipments, 0) }),
    );
  if (p.fees.unknownPayments > 0)
    notes.push(
      t('admin.financeReport.notes.feesUnknown', { count: formatNumber(p.fees.unknownPayments, 0) }),
    );
  if (p.ads.accounts === 0) notes.push(t('admin.financeReport.notes.adsMissing'));
  if (p.ads.fxMissing > 0) notes.push(t('admin.financeReport.notes.adsFx'));
  if (p.disputes.amount > 0)
    notes.push(
      t('admin.financeReport.notes.disputes', {
        lost: tl(p.disputes.lost),
        count: formatNumber(p.disputes.count, 0),
        fees: tl(p.disputes.fees),
      }),
    );
  const empty = <td className={td} />;
  return (
    <div className="mt-8">
      <Section title={t('admin.financeReport.pnlTitle')} help={<HelpTip k="finance_report.status" />}>
        <Table
          caption={t('admin.financeReport.pnlTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.financeReport.pnlCols.item')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.financeReport.pnlCols.amount')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.financeReport.pnlCols.share')}
              </th>
              <th scope="col" className={th}>
                {t('admin.financeReport.pnlCols.status')}
              </th>
            </tr>
          }
        >
          <AmountRow
            label={t('admin.financeReport.rows.netSales')}
            amount={tl(p.netSales)}
            sub
            extra={
              <>
                {empty}
                {empty}
              </>
            }
          />
          {rows.map((r) => (
            <AmountRow
              key={r.k}
              label={t(`admin.financeReport.rows.${r.k}`)}
              amount={r.amount}
              strong={r.strong}
              extra={
                <>
                  <td className={`${tdR} text-ink-2`}>{r.amount === '—' ? '—' : share(r.value)}</td>
                  <td className={td}>{r.status && <StatusBadge status={r.status} />}</td>
                </>
              }
            />
          ))}
          {categories.map(([c, v]) => (
            <AmountRow
              key={c}
              label={t(`admin.financeInputs.categories.${c}`)}
              amount={cost(v)}
              sub
              extra={
                <>
                  <td className={`${tdR} text-ink-3`}>{share(v)}</td>
                  {empty}
                </>
              }
            />
          ))}
          <AmountRow
            label={t('admin.financeReport.rows.operatingProfit')}
            amount={tl(p.operatingProfit)}
            strong
            extra={
              <>
                <td className={`${tdR} text-ink-2`}>{share(p.operatingProfit)}</td>
                {empty}
              </>
            }
          />
        </Table>
      </Section>
      {notes.length > 0 && (
        <div className="mt-2 max-w-[80ch] space-y-1 text-sm text-ink-2">
          {notes.map((n) => (
            <p key={n}>{n}</p>
          ))}
          <p>
            <Link to="/finans-girdileri" className="font-medium text-pine hover:underline">
              {t('admin.financeReport.goInputs')}
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

function CashTable({ d, filtered }: { d: FinanceReport; filtered: boolean }) {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const c = d.cash;
  const tl = (n: number) => money(n, 'TRY');
  const out = (n: number) => money(n === 0 ? 0 : -n, 'TRY');
  const methods = Object.entries(c.inByMethod).sort((a, b) => b[1] - a[1]);
  const head = (
    <tr>
      <th scope="col" className={th}>
        {t('admin.financeReport.pnlCols.item')}
      </th>
      <th scope="col" className={thR}>
        {t('admin.financeReport.pnlCols.amount')}
      </th>
    </tr>
  );
  return (
    <Section title={t('admin.financeReport.cashTitle')} help={<HelpTip k="finance_report.cash" />}>
      <Table caption={t('admin.financeReport.cashTitle')} head={head}>
        <AmountRow label={t('admin.financeReport.cashRows.in')} amount={tl(c.inflow)} strong />
        {methods.map(([m, v]) => (
          <AmountRow
            key={m}
            label={t('admin.financeReport.inByMethod', { name: t(`admin.financeInputs.providers.${m}`) })}
            amount={tl(v)}
            sub
          />
        ))}
        <AmountRow
          label={t('admin.financeReport.cashRows.dealerCollections')}
          amount={tl(c.dealerCollections)}
          sub
        />
        <AmountRow label={t('admin.financeReport.cashRows.out')} amount={out(c.outflow)} strong />
        <AmountRow label={t('admin.financeReport.cashRows.refunds')} amount={out(c.refunds)} sub />
        <AmountRow label={t('admin.financeReport.cashRows.fees')} amount={out(c.fees)} sub />
        <AmountRow label={t('admin.financeReport.cashRows.shipping')} amount={out(c.shipping)} sub />
        <AmountRow label={t('admin.financeReport.cashRows.expenses')} amount={out(c.expenses)} sub />
        <AmountRow label={t('admin.financeReport.cashRows.ads')} amount={out(c.ads)} sub />
        <AmountRow label={t('admin.financeReport.cashRows.suppliers')} amount={out(c.suppliers)} sub />
        {c.disputes > 0 && (
          <AmountRow label={t('admin.financeReport.cashRows.disputes')} amount={out(c.disputes)} sub />
        )}
        <AmountRow label={t('admin.financeReport.cashRows.net')} amount={tl(c.net)} strong />
      </Table>
      <div className="mt-2 space-y-1 text-xs text-ink-3">
        <p>{t('admin.financeReport.cashNote')}</p>
        {filtered && <p>{t('admin.financeReport.dealerCollectionsHidden')}</p>}
        {c.fxMissing > 0 && (
          <p role="alert" className="text-amber">
            {t('admin.financeReport.cashFx', { count: formatNumber(c.fxMissing, 0) })}
          </p>
        )}
      </div>
    </Section>
  );
}

function VatTable({ d }: { d: FinanceReport }) {
  const t = useT();
  const money = useMoney();
  const v = d.vat;
  const tl = (n: number) => money(n, 'TRY');
  return (
    <Section title={t('admin.financeReport.vatTitle')} help={<HelpTip k="finance_report.vat" />}>
      <Table
        caption={t('admin.financeReport.vatTitle')}
        head={
          <tr>
            <th scope="col" className={th}>
              {t('admin.financeReport.pnlCols.item')}
            </th>
            <th scope="col" className={thR}>
              {t('admin.financeReport.pnlCols.amount')}
            </th>
          </tr>
        }
      >
        <AmountRow label={t('admin.financeReport.vatRows.output')} amount={tl(v.output)} />
        <AmountRow label={t('admin.financeReport.vatRows.inputExpenses')} amount={tl(v.inputExpenses)} />
        <AmountRow label={t('admin.financeReport.vatRows.inputShipping')} amount={tl(v.inputShipping)} />
        {v.inputPurchases !== null && (
          <AmountRow label={t('admin.financeReport.vatRows.inputPurchases')} amount={tl(v.inputPurchases)} />
        )}
        <AmountRow
          label={t('admin.financeReport.vatRows.difference')}
          amount={tl(v.output - v.inputExpenses - v.inputShipping - (v.inputPurchases ?? 0))}
          strong
        />
      </Table>
      <p className="mt-2 text-xs text-ink-3">{t('admin.financeReport.vatNote')}</p>
    </Section>
  );
}

function Balances({ d }: { d: FinanceReport }) {
  const t = useT();
  const { can } = usePermissions();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const b = d.balances;
  return (
    <Section title={t('admin.financeReport.balancesTitle')}>
      <dl className="divide-y divide-line rounded-md border border-line text-sm">
        <div className="flex flex-wrap justify-between gap-2 px-3 py-2">
          <dt>
            {t('admin.financeReport.balanceRows.openDisputes', { count: formatNumber(b.openDisputes, 0) })}{' '}
            {b.openDisputes > 0 && can('payments.payment.read') && (
              <Link to="/odeme-itirazlari" className="text-pine hover:underline">
                {t('admin.financeReport.openDisputesLink')}
              </Link>
            )}
          </dt>
          <dd className="tabular">{money(b.openDisputesAmount, 'TRY')}</dd>
        </div>
        <div className="flex flex-wrap justify-between gap-2 px-3 py-2">
          <dt>
            {t('admin.financeReport.balanceRows.dealerReceivables', {
              count: formatNumber(b.dealersWithBalance, 0),
            })}
          </dt>
          <dd className="tabular">
            {b.dealerReceivables === null ? (
              <span className="text-ink-3">{t('admin.financeReport.dealerScopeHidden')}</span>
            ) : (
              money(b.dealerReceivables, 'TRY')
            )}
          </dd>
        </div>
        <div className="flex flex-wrap justify-between gap-2 px-3 py-2">
          <dt>
            {t('admin.financeReport.balanceRows.codInTransit', { count: formatNumber(b.codOrders, 0) })}
          </dt>
          <dd className="tabular">{money(b.codInTransit, 'TRY')}</dd>
        </div>
        <div className="flex flex-wrap justify-between gap-2 px-3 py-2">
          <dt>
            {t('admin.financeReport.balanceRows.supplierPayables', {
              count: formatNumber(b.suppliersWithBalance, 0),
              overdue: money(b.supplierOverdue, 'TRY'),
            })}
          </dt>
          <dd className="tabular">
            {b.supplierPayables === null ? (
              <span className="text-ink-3">{t('admin.financeReport.dealerScopeHidden')}</span>
            ) : (
              money(b.supplierPayables, 'TRY')
            )}
          </dd>
        </div>
      </dl>
    </Section>
  );
}

function FeesTable({ d }: { d: FinanceReport }) {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const rows = [...d.profit.fees.byProvider].sort((a, b) => b.processed - a.processed);
  return (
    <Section title={t('admin.financeReport.feesTitle')}>
      {!rows.length ? (
        <EmptyState title={t('admin.financeReport.noData')} />
      ) : (
        <Table
          caption={t('admin.financeReport.feesTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.financeReport.feeCols.provider')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.financeReport.feeCols.processed')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.financeReport.feeCols.fee')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.financeReport.feeCols.noRule')}
              </th>
            </tr>
          }
        >
          {rows.map((r) => (
            <tr key={r.provider} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-normal`}>
                {t(`admin.financeInputs.providers.${r.provider}`)}
              </th>
              <td className={tdR}>{money(r.processed, 'TRY')}</td>
              <td className={tdR}>{money(r.fee, 'TRY')}</td>
              <td className={tdR}>{formatNumber(r.withoutRule, 0)}</td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

function ChannelsTable({ d }: { d: FinanceReport }) {
  const t = useT();
  const money = useMoney();
  const tl = (n: number) => money(n, 'TRY');
  return (
    <Section title={t('admin.financeReport.channelsTitle')} help={<HelpTip k="finance_report.products" />}>
      {!d.byChannel.length ? (
        <EmptyState title={t('admin.financeReport.noData')} />
      ) : (
        <Table
          caption={t('admin.financeReport.channelsTitle')}
          head={
            <tr>
              {(['channel', 'revenue', 'cogs', 'shipping', 'fees', 'contribution'] as const).map((k) => (
                <th key={k} scope="col" className={k === 'channel' ? th : thR}>
                  {t(`admin.financeReport.channelCols.${k}`)}
                </th>
              ))}
            </tr>
          }
        >
          {d.byChannel.map((r) => (
            <tr key={r.channel} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-normal`}>
                {t(`admin.salesReport.channels.${r.channel}`)}
                {r.unknownCostRevenue > 0 && (
                  <span className="block text-xs text-ink-3">{t('admin.financeReport.unknownCost')}</span>
                )}
              </th>
              <td className={tdR}>{tl(r.revenue)}</td>
              <td className={tdR}>{tl(r.cogs)}</td>
              <td className={tdR}>{tl(r.shipping)}</td>
              <td className={tdR}>{tl(r.fees)}</td>
              <td className={`${tdR} font-medium`}>{tl(r.contribution)}</td>
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

function ProductsTable({ d }: { d: FinanceReport }) {
  const t = useT();
  const money = useMoney();
  const pct = usePct();
  const tl = (n: number) => money(n, 'TRY');
  return (
    <div className="mt-8">
      <Section title={t('admin.financeReport.productsTitle')} help={<HelpTip k="finance_report.products" />}>
        {!d.byProduct.length ? (
          <EmptyState title={t('admin.financeReport.noProducts')} />
        ) : (
          <>
            {/* Kaydırılan bölge klavyeyle odaklanabilir (WCAG 2.1.1). */}
            <div
              className="max-h-[32rem] overflow-y-auto"
              tabIndex={0}
              role="region"
              aria-label={t('admin.financeReport.productsTitle')}
            >
              <Table
                caption={t('admin.financeReport.productsTitle')}
                head={
                  <tr>
                    {(['product', 'revenue', 'cogs', 'grossProfit', 'margin'] as const).map((k) => (
                      <th key={k} scope="col" className={k === 'product' ? th : thR}>
                        {t(`admin.financeReport.productCols.${k}`)}
                      </th>
                    ))}
                  </tr>
                }
              >
                {d.byProduct.map((r) => (
                  <tr key={r.productId ?? r.title} className="border-t border-line">
                    <th scope="row" className={`${td} text-start font-normal`}>
                      {r.productId ? (
                        <Link
                          to="/urunler/$id"
                          params={{ id: r.productId }}
                          className="font-medium text-pine hover:underline"
                        >
                          {r.title}
                        </Link>
                      ) : (
                        r.title
                      )}
                      {r.unknownCostRevenue > 0 && (
                        <span className="block text-xs text-ink-3">
                          {t('admin.financeReport.unknownCost')}: {tl(r.unknownCostRevenue)}
                        </span>
                      )}
                      {r.estimated && (
                        <span className="block text-xs text-ink-3">
                          {t('admin.financeReport.estimatedCost')}
                        </span>
                      )}
                    </th>
                    <td className={tdR}>{tl(r.revenue)}</td>
                    <td className={tdR}>{tl(r.cogs)}</td>
                    <td className={tdR}>{tl(r.grossProfit)}</td>
                    <td className={tdR}>{pct(r.marginBps)}</td>
                  </tr>
                ))}
              </Table>
            </div>
            {d.byProduct.length >= 100 && (
              <p className="mt-2 text-xs text-ink-3">
                {t('admin.financeReport.showingProducts', { count: d.byProduct.length })}
              </p>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

function MissingCosts({ coverage, error }: { coverage: CostCoverage | undefined; error: unknown }) {
  const t = useT();
  const { formatNumber } = useI18n();
  return (
    <Section title={t('admin.financeReport.missingTitle')}>
      {error ? (
        <p role="alert" className="text-sm text-rose">
          {error instanceof ApiError ? error.title : String(error)}
        </p>
      ) : !coverage ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <p className="mb-2 text-xs text-ink-3">
            {t('admin.financeReport.coverage', {
              snapshot: formatNumber(coverage.lines.snapshot, 0),
              late: formatNumber(coverage.lines.late, 0),
              missing: formatNumber(coverage.lines.missing, 0),
            })}
          </p>
          {!coverage.missing.length ? (
            <EmptyState title={t('admin.financeReport.missingNone')} />
          ) : (
            <>
              <p className="mb-2 max-w-[80ch] text-sm text-ink-2">{t('admin.financeReport.missingLead')}</p>
              <Table
                caption={t('admin.financeReport.missingTitle')}
                head={
                  <tr>
                    <th scope="col" className={th}>
                      {t('admin.financeReport.missingCols.product')}
                    </th>
                    <th scope="col" className={th}>
                      {t('admin.financeReport.missingCols.sku')}
                    </th>
                    <th scope="col" className={thR}>
                      {t('admin.financeReport.missingCols.quantity')}
                    </th>
                  </tr>
                }
              >
                {coverage.missing.map((m) => (
                  <tr key={m.variantId ?? m.sku} className="border-t border-line">
                    <th scope="row" className={`${td} text-start font-normal`}>
                      {m.title}
                    </th>
                    <td className={td}>{m.sku}</td>
                    <td className={tdR}>{formatNumber(m.quantity, 0)}</td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </>
      )}
    </Section>
  );
}

function Reconciliation({ d }: { d: FinanceReport }) {
  const t = useT();
  const { formatDate, formatNumber } = useI18n();
  const me = useMe();
  // Mutabakat işletme genelidir; mağaza kapsamı sınırlı kullanıcıya gösterilmez (sunucu da boş döner).
  if (!me.data || me.data.storeScope.length) return null;
  return (
    <Section title={t('admin.financeReport.reconTitle')}>
      {!d.reconciliation.length ? (
        <EmptyState title={t('admin.financeReport.reconEmpty')} />
      ) : (
        <Table
          caption={t('admin.financeReport.reconTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.financeReport.reconCols.provider')}
              </th>
              <th scope="col" className={th}>
                {t('admin.financeReport.reconCols.status')}
              </th>
              <th scope="col" className={th}>
                {t('admin.financeReport.reconCols.finished')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.financeReport.reconCols.open')}
              </th>
            </tr>
          }
        >
          {d.reconciliation.map((r) => (
            <tr key={r.provider} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-normal`}>
                {t(`admin.financeInputs.providers.${r.provider}`)}
              </th>
              <td className={td}>
                <Badge tone={r.status === 'ok' ? 'moss' : r.status === 'running' ? 'slate' : 'amber'}>
                  {t(`admin.financeReport.reconStatus.${r.status}`)}
                </Badge>
              </td>
              <td className={td}>{formatDate(r.finishedAt)}</td>
              <td className={tdR}>{formatNumber(r.openItems, 0)}</td>
            </tr>
          ))}
        </Table>
      )}
      <p className="mt-2 text-sm">
        <Link to="/odemeler" className="font-medium text-pine hover:underline">
          {t('admin.financeReport.openPayments')}
        </Link>
      </p>
    </Section>
  );
}
